import clsx from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  TemplateMessageData,
  TemplateResponseData,
} from "@smart/bridge-templates-basic";
import {
  Button,
  ContextMenu,
  Select,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import { extractId } from "@smart/itops-types-basic";
import { useUser } from "@smart/manage-hooks-dom";

import { ClearForm } from "./clear-form";
import { EditFormInfoModal } from "./edit-form-info-modal";
import { MessageTemplates, TranslationModal, VersionHistory } from "./settings";
import { copySupportInformation } from "../../../shared";
import {
  LanguageKey,
  SupportedLanguageOption,
  baseLanguageKey,
  baseLanguageLabel,
} from "../../../types";
import { EditingFormInfo } from "../../list-forms/form";
import { usePrintForm } from "../../shared/print-form";
import {
  GqlField,
  GqlFormCategory,
  GqlGroup,
  GqlMatterType,
  GqlSetting,
} from "../../types";
import { GqlSubmissionLinkHash } from "../types";

type ActionsProps = {
  formUri: string;
  formTitle: string;
  isMatterSpecific: boolean;
  isFormActive: boolean | undefined;
  canActivate?: boolean;
  canUpdate?: boolean;
  disabled?: boolean;
  loading: {
    form: boolean;
    matterTypes: boolean;
  };
  onActiveToggleClick: () => Promise<void>;
  saveMessageTemplates: (messages: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string;
    confirmationMode?: "message" | "redirect";
    redirectUrl?: string | null;
  }) => Promise<void>;
  currentClientMessages?: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
  };
  currentRedirectUrl?: string | null;
  defaultClientMessage?: {
    matter?: string | null;
    lead?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
  } | null;
  messagePlaceholderData: Partial<TemplateMessageData>;
  responsePlaceholderData: Partial<TemplateResponseData>;
  isPreview: boolean;
  clearIntakeForm: () => void;
  formCategory?: GqlFormCategory;
  creationStatus?: string | null;
  onSaveLanguages: (languages: LanguageKey[]) => Promise<void>;
  supportedLanguages?: SupportedLanguageOption[];
  viewingLanguage: LanguageKey;
  setViewingLanguage: (language: LanguageKey) => void;
  updateFormInfo: (values: EditingFormInfo) => Promise<void>;
  setting?: GqlSetting | null;
  matterTypeLocations: string[];
  matterTypes: GqlMatterType[];
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
};

const Actions = ({
  formUri,
  formTitle,
  formCategory,
  isMatterSpecific,
  isFormActive,
  canActivate,
  disabled,
  loading,
  canUpdate,
  onActiveToggleClick,
  saveMessageTemplates,
  currentClientMessages,
  currentRedirectUrl,
  defaultClientMessage,
  messagePlaceholderData,
  responsePlaceholderData,
  isPreview,
  clearIntakeForm,
  creationStatus,
  onSaveLanguages,
  supportedLanguages,
  viewingLanguage,
  setViewingLanguage,
  updateFormInfo,
  setting,
  matterTypeLocations,
  matterTypes,
  groups,
  fields,
  submissionLinkHashes,
}: ActionsProps) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [settingsModal, showSettingsModal] = useState<
    "messageTemplates" | "versionHistory" | "configuration" | "languages" | null
  >(null);
  const [clearFormModal, showClearFormModal] = useState(false);
  const [printFormUri, setPrintFormUri] = useState<string | undefined>(
    undefined,
  );
  const { printFormContent } = usePrintForm({
    formUri: printFormUri,
    onPrinted: () => setPrintFormUri(undefined),
  });

  return (
    <div
      className={clsx(
        "flex justify-end items-center gap-large flex-1",
        isMatterSpecific && "gap-small",
      )}
    >
      {isPreview ? (
        <div className="flex items-center gap-small">
          <Button
            text="Print"
            variant="secondaryDefault"
            onClick={() => setPrintFormUri(formUri)}
          />
          {printFormContent}
          <Button
            text="Clear form"
            variant="secondaryDefault"
            type="reset"
            onClick={() => showClearFormModal(true)}
          />
          <ClearForm
            onSubmit={() => {
              clearIntakeForm();
              showClearFormModal(false);
            }}
            onClose={() => showClearFormModal(false)}
            open={clearFormModal}
          />
        </div>
      ) : (
        <>
          {!isMatterSpecific && (
            <div className="flex gap-small">
              <Switch
                dataTestId="active-toggle"
                disabled={
                  !canActivate || disabled || (loading.form && !isFormActive)
                }
                toggled={!!isFormActive}
                onClick={() => onActiveToggleClick()}
              />
              <div className="text-ui">Active</div>
            </div>
          )}
          {!!supportedLanguages?.length && (
            <Select
              dataTestId="select-viewing-languages"
              placeholder="Languages"
              options={[
                ...supportedLanguages.map((language) => ({
                  label: language.label,
                  value: language.key,
                })),
                { label: baseLanguageLabel, value: baseLanguageKey },
              ].sort((a, b) => a.label.localeCompare(b.label))}
              value={viewingLanguage}
              onChange={setViewingLanguage}
            />
          )}
          {canUpdate && (
            <ContextMenu
              button={{
                variant: "secondaryDefault",
                leftIcon: "regularGear",
                size: "small",
                dataTestId: "settings-menu-button",
                disabled,
              }}
              placement="bottom-end"
              offsetBy={5}
              items={[
                {
                  label: "Configuration",
                  onClick: () => showSettingsModal("configuration"),
                  hidden: isMatterSpecific,
                },
                {
                  label: "Languages",
                  onClick: () => showSettingsModal("languages"),
                },
                {
                  label: "Message Templates",
                  onClick: () => showSettingsModal("messageTemplates"),
                },
                {
                  label: "Version History",
                  onClick: () => showSettingsModal("versionHistory"),
                },
                {
                  label: "Copy Support Information",
                  onClick: () => {
                    copySupportInformation({
                      teamUri: user?.teamUri,
                      formUri,
                      userUri: user?.userUri,
                    });
                  },
                  internal: true,
                },
              ]}
            />
          )}
          {isMatterSpecific && (
            <>
              <Button
                onClick={() =>
                  navigate(`/embedded/tab?sharingFormId=${extractId(formUri)}`)
                }
                text="Share"
                leftIcon="regularShare"
                variant="primaryDefault"
                disabled={disabled}
              />
              <Button
                onClick={() =>
                  navigate(
                    `/embedded/tab?sharingFormId=${extractId(formUri)}&isInternal=true`,
                  )
                }
                text="Prefill for client"
                leftIcon="regularPencil"
                variant="primaryDefault"
                disabled={disabled}
              />
            </>
          )}
          <MessageTemplates
            formCategory={formCategory}
            messageTemplatesModal={settingsModal === "messageTemplates"}
            onClose={() => showSettingsModal(null)}
            onSave={saveMessageTemplates}
            currentMessages={currentClientMessages}
            currentRedirectUrl={currentRedirectUrl}
            defaultMessages={defaultClientMessage}
            messagePlaceholderData={messagePlaceholderData}
            responsePlaceholderData={responsePlaceholderData}
          />
          <VersionHistory
            formUri={formUri}
            formTitle={formTitle}
            formCategory={formCategory}
            confirmationMessage={currentClientMessages?.confirmation || ""}
            creationStatus={creationStatus}
            versionHistoryModal={settingsModal === "versionHistory"}
            onClose={() => showSettingsModal(null)}
          />
          <TranslationModal
            open={settingsModal === "languages"}
            onClose={() => showSettingsModal(null)}
            onSaveLanguages={onSaveLanguages}
            supportedLanguages={supportedLanguages}
            formTitle={formTitle}
          />
          <EditFormInfoModal
            loading={loading.form || loading.matterTypes}
            open={settingsModal === "configuration"}
            onClose={() => showSettingsModal(null)}
            updateFormInfo={updateFormInfo}
            setting={setting}
            category={formCategory}
            matterTypeLocations={matterTypeLocations}
            matterTypes={matterTypes}
            groups={groups}
            fields={fields}
            submissionLinkHashes={submissionLinkHashes}
          />
        </>
      )}
    </div>
  );
};

export { Actions };
